import type {
  BustlyWorkspaceIntegrationsOptions,
  DesktopUpdateStatusPayload,
  RendererHostAdapter,
} from "./host";
import { buildElectronHostCapabilities, normalizeHostOs } from "./host-capabilities";

function requireElectronApi(): ElectronAPI {
  if (!window.electronAPI) {
    throw new Error("Electron API not available");
  }
  return window.electronAPI;
}

export const electronRendererHostAdapter: RendererHostAdapter = {
  platform: "electron",
  gatewayStatus: async () => await requireElectronApi().gatewayStatus(),
  gatewayStart: async () => await requireElectronApi().gatewayStart(),
  gatewayConnectConfig: async () => await requireElectronApi().gatewayConnectConfig(),
  gatewayRestoreLastGoodConfig: async () =>
    await requireElectronApi().gatewayRestoreLastGoodConfig(),
  openclawInit: async (options?: PresetConfigOptions) => await requireElectronApi().openclawInit(options),
  openclawIsInitialized: async () => await requireElectronApi().openclawIsInitialized(),
  getAppInfo: async () => await requireElectronApi().getAppInfo(),
  getHostCapabilities: async () => {
    const appInfo = await requireElectronApi().getAppInfo();
    return buildElectronHostCapabilities(normalizeHostOs(appInfo.osPlatform));
  },
  bustlyIsLoggedIn: async () => await requireElectronApi().bustlyIsLoggedIn(),
  bustlyLogin: async () => await requireElectronApi().bustlyLogin(),
  bustlyPollLogin: async (loginTraceId: string) =>
    await requireElectronApi().bustlyPollLogin(loginTraceId),
  bustlyCancelLogin: async (loginTraceId?: string) =>
    await requireElectronApi().bustlyCancelLogin(loginTraceId),
  bustlyGetUserInfo: async () => await requireElectronApi().bustlyGetUserInfo(),
  bustlyLogout: async () => await requireElectronApi().bustlyLogout(),
  bustlyOpenSettings: async (workspaceId?: string) =>
    await requireElectronApi().bustlyOpenSettings(workspaceId),
  bustlyOpenWorkspaceSettings: async (workspaceId: string) =>
    await requireElectronApi().bustlyOpenWorkspaceSettings(workspaceId),
  bustlyOpenWorkspaceIntegrations: async (
    workspaceId: string,
    options?: BustlyWorkspaceIntegrationsOptions,
  ) =>
    await requireElectronApi().bustlyOpenWorkspaceIntegrations(workspaceId, options),
  bustlyOpenWorkspaceInvite: async (workspaceId: string) =>
    await requireElectronApi().bustlyOpenWorkspaceInvite(workspaceId),
  bustlyOpenWorkspaceManage: async (workspaceId: string) =>
    await requireElectronApi().bustlyOpenWorkspaceManage(workspaceId),
  bustlyOpenWorkspacePricing: async (workspaceId: string) =>
    await requireElectronApi().bustlyOpenWorkspacePricing(workspaceId),
  bustlyOpenWorkspaceCreate: async (workspaceId?: string) =>
    await requireElectronApi().bustlyOpenWorkspaceCreate(workspaceId),
  resolvePastedPath: async (params) => await requireElectronApi().resolvePastedPath(params),
  selectChatContextPaths: async () => await requireElectronApi().selectChatContextPaths(),
  resolveChatImagePreview: async (path: string) => await requireElectronApi().resolveChatImagePreview(path),
  resolveChatMediaPreview: async (path: string) => await requireElectronApi().resolveChatMediaPreview(path),
  showChatImageContextMenu: async (params) => await requireElectronApi().showChatImageContextMenu(params),
  openLocalPath: async (path: string) => await requireElectronApi().openLocalPath(path),
  revealLocalPath: async (path: string) => await requireElectronApi().revealLocalPath(path),
  openExternalUrl: async (url: string) => await requireElectronApi().openExternalUrl(url),
  getNativeFullscreenStatus: async () => await requireElectronApi().getNativeFullscreenStatus(),
  updaterCheck: async () => await requireElectronApi().updaterCheck(),
  updaterStartInstall: async () => await requireElectronApi().updaterStartInstall(),
  updaterInstall: async () => await requireElectronApi().updaterInstall(),
  updaterStatus: async () => await requireElectronApi().updaterStatus(),
  consumePendingDeepLink: async () => await requireElectronApi().consumePendingDeepLink(),
  logRendererDiagnostic: async (payload) => {
    await requireElectronApi().rendererLog(payload);
  },
  onGatewayLifecycle: (callback: (data: GatewayLifecycleData) => void) =>
    requireElectronApi().onGatewayLifecycle(callback),
  onBustlyLoginRefresh: (callback: () => void) =>
    requireElectronApi().onBustlyLoginRefresh(callback),
  onBustlyLoginProgress: (callback: () => void) =>
    requireElectronApi().onBustlyLoginProgress(callback),
  onNativeFullscreenChange: (callback: (data: { isNativeFullscreen: boolean }) => void) =>
    requireElectronApi().onNativeFullscreenChange(callback),
  onUpdateStatus: (callback: (data: DesktopUpdateStatusPayload) => void) =>
    requireElectronApi().onUpdateStatus(callback),
  onDeepLink: (callback: (data: DeepLinkData) => void) =>
    requireElectronApi().onDeepLink(callback),
};
